import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import Modal from 'ui/layout/Modal'
import { RadioGroup, TextField } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { Theme } from '@mui/material/styles'
import PosterPrintButton from 'ui/utils/PosterPrintButton'
import { MedicalInstitutionProps } from 'types/med'
import Stack from 'ui/layout/Stack'
import { StackItem } from 'ui/layout/StackItem'
import RadioButtonWithLabel from 'ui/partials/RadioButtonWithLabel'
import type { PosterDesignNames } from 'ui/utils/PosterPrintButton'
import MarginRightCancelButton from 'ui/shorthands/MarginRightCancelButton'

const useStyles = makeStyles()((theme: Theme) => ({
  form: {
    padding: theme.spacing(1, 0),
  },
  message: {
    display: 'block',
  },
  buttons: {
    display: 'flex',
    placeContent: 'flex-end',
    alignItems: 'center',
  },
  button: {
    height: 24,
    minWidth: 64,
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'row',
  },
}))

const posterDesigns: { name: PosterDesignNames; label: string }[] = [
  {
    name: 'reservation',
    label: i18n.t('診療'),
  },
  {
    name: 'medication',
    label: i18n.t('服薬指導'),
  },
]

// NOTE: 入力値を1行全角14文字以内、2行まで強制フォーマット
const formatPosterText = (value: string) => {
  const [line1, line2] = value.split('\n')
  let newLine1 = line1
  let newLine2 = line2
  if (value.replace('\n', '')?.length <= 14) {
    if (line2 === undefined) {
      return newLine1
    }
    return [newLine1, newLine2].join('\n')
  }
  if (newLine1?.length > 14) {
    newLine1 = line1.slice(0, 14)
    newLine2 = [line1.slice(14), line2].join('')
  }
  if (newLine2?.length >= 14) {
    newLine2 = newLine2.slice(0, 14)
  }
  return [newLine1, newLine2].join('\n')
}

type ConfirmDiscardModalProps = {
  open: boolean
  med: MedicalInstitutionProps
  onClose: () => void
}
const PosterPrintModal: React.FC<ConfirmDiscardModalProps> = ({
  open,
  med,
  onClose,
}: ConfirmDiscardModalProps) => {
  const { t } = useTranslation()
  const { classes } = useStyles()

  const [text, setText] = useState<string>()
  const [design, setDesign] = useState<PosterDesignNames>('reservation')

  useEffect(() => {
    setText(undefined)
    setDesign('reservation')
  }, [onClose])

  return (
    <Modal open={open} handleClose={onClose} large>
      <div className={classes.form}>
        <StackItem
          label={t('施設名')}
          description={t('1行全角14文字です。2行まで入力できます。')}
          alignTop
        >
          <TextField
            variant="outlined"
            fullWidth
            multiline
            rows={2}
            maxRows={2}
            placeholder={t('placeholder.YaDocクリニック\n水天宮前診療所')}
            value={text}
            onChange={(e) => setText(formatPosterText(e.target.value))}
          />
        </StackItem>
        <StackItem label={t('用途種別')}>
          <RadioGroup
            className={classes.radioGroup}
            onChange={(e) => {
              const value = e.target.value
              if (value === 'reservation' || value === 'medication') {
                setDesign(value)
              }
            }}
            value={design}
          >
            {posterDesigns.map((s) => (
              <RadioButtonWithLabel
                key={`${s.label}${s.name}`}
                value={s.name}
                label={s.label}
              />
            ))}
          </RadioGroup>
        </StackItem>
      </div>
      <Stack className={classes.buttons} borderTop>
        <MarginRightCancelButton onClick={onClose} />
        {med.form_url && (
          <PosterPrintButton
            text={text}
            url={med.form_url}
            design={design}
            disabled={!text}
            variant={'contained'}
            color="primary"
          />
        )}
      </Stack>
    </Modal>
  )
}
export default PosterPrintModal
